// deps
import React, { memo, useCallback, useMemo } from "react";
import classNames from "classnames";

// internal-deps
import { Tick, ThreeDotAnimation } from "@swiggy-private/core-ui";

// interfaces
import { CredPaymentMethod, CredPayPaymentGroup } from "@payment-ui/interfaces/CredPay";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// widgets
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";
import Alert from "@payment-ui/widgets/alert/Alert";

// components
import PayButton from "./components/PayButton";

// helpers
import { canEnablePaymentMethod, getPaymentMethodIconUrl } from "@payment-ui/helpers/Payment";
import { getCredAriaLabel } from "@payment-ui/helpers/Cred";

// data testids
import { CRED_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

import styles from "./Cred.v4.scss";

const LOADING_OFFER_CTA = "Checking offer Details";

export interface IProps extends PaymentMethodProps {
    key: string;
    methodData: CredPaymentMethod;
    paymentActions: CredPayPaymentGroup["paymentActions"];
    bannerText: string;
    isEligible: boolean;
    isSdkAvailable: boolean;
    isOfferLoading: boolean;
}

function CredPayMethod(props: IProps): React.ReactElement {
    const { onSelect, onPay } = props.paymentActions;

    const { meta } = props.methodData;

    const isMethodEnabled = useMemo(() => {
        return canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled) && props.isSdkAvailable;
    }, [props.isPaymentGroupEnabled, props.methodData, props.isSdkAvailable]);

    //Only show alert if method disabled
    const showAlert = useMemo(() => {
        return !canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled);
    }, [props.isPaymentGroupEnabled, props.methodData]);

    const iconUrl = useMemo(() => getPaymentMethodIconUrl(props.methodData), [props.methodData]);

    const containerClass = classNames(styles.paymentMethod, {
        [styles.paymentMethodDisabled]: !isMethodEnabled,
    });

    //Default Promo Msg by backend
    const promoMsg = useMemo(() => {
        return !showAlert ? meta.promoMsg : "";
    }, [meta.promoMsg, showAlert]);

    //Cred Specific Promo Msg By eligiblity call
    const dynamicPromoMsg = useMemo(() => {
        if (showAlert) {
            return;
        }
        const message = props.isOfferLoading ? (
            <div>
                {meta.loadingOfferMessage ?? LOADING_OFFER_CTA} <ThreeDotAnimation />
            </div>
        ) : (
            <div>{props.bannerText}</div>
        );
        return message;
    }, [props.bannerText, props.isOfferLoading, showAlert, meta.loadingOfferMessage]);

    const tickHidden = useMemo(() => {
        return !isMethodEnabled ? styles.hideTick : "";
    }, [isMethodEnabled]);

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            if (!isMethodEnabled) {
                return;
            }
            onSelect(props.methodData);
        },
        [isMethodEnabled, props.methodData, onSelect],
    );

    const CredAriaLabel = useMemo(() => getCredAriaLabel(props.methodData, isMethodEnabled), [
        props.methodData,
        isMethodEnabled,
    ]);

    return (
        <div className={containerClass} onClick={onClick} data-testid={CRED_PAYMENT_METHOD_TEST_IDS.CONTAINER}>
            <div className={styles.paymentMethodContainer}>
                <PaymentMethodIcon src={iconUrl} data-testid={CRED_PAYMENT_METHOD_TEST_IDS.ICON} />

                <PaymentMethodName
                    displayName={props.methodData.displayName}
                    promoMsg={promoMsg}
                    enabled={isMethodEnabled}
                    ariaLabel={CredAriaLabel}
                    data-testid={CRED_PAYMENT_METHOD_TEST_IDS.NAME}
                    dynamicPromoMsg={dynamicPromoMsg}
                >
                    {showAlert && (
                        <Alert
                            className={styles.paymentMethodDisable}
                            message={props.methodData.meta.disableMessage}
                            data-testid={CRED_PAYMENT_METHOD_TEST_IDS.DISABLE_MESSAGE}
                        />
                    )}
                </PaymentMethodName>

                <Tick
                    className={tickHidden}
                    selected={props.selected}
                    data-testid={CRED_PAYMENT_METHOD_TEST_IDS.TICK}
                />
            </div>

            {props.selected && isMethodEnabled && (
                <PayButton
                    enabled={isMethodEnabled}
                    selected={props.selected}
                    methodData={props.methodData}
                    onPay={onPay}
                />
            )}
        </div>
    );
}

export default memo(CredPayMethod);
